import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Image,
  Keyboard,
  type LayoutChangeEvent,
  type KeyboardEvent,
  type NativeSyntheticEvent,
  ScrollView,
  TextInput,
  type TextInputContentSizeChangeEventData,
  useWindowDimensions,
} from 'react-native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Send, Trash2 } from 'lucide-react-native';
import { StyleSheet } from 'react-native-unistyles';
import { Redirect, useIsFocused } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import AppHeader from '@/components/AppHeader';
import NativeTabFocusGate from '@/components/navigation/NativeTabFocusGate';
import { Avatar, AvatarImage } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Box, PressableBox, Text } from '@/components/ui/primitives';
import { useLeagueContext } from '@/contexts/LeagueContext';
import { useAuth } from '@/hooks/useAuth';
import type { PickedImage } from '@/hooks/useImagePicker';
import { useImagePicker } from '@/hooks/useImagePicker';
import { useToast } from '@/hooks/useToast';
import { buildAuthHeaders, apiRequestJson } from '@/lib/queryClient';
import { useTRPC, useTRPCClient } from '@/lib/trpc';
import { config } from '@/config/env';
import { isIOS, platformOS } from '@/lib/platform';
import { getLeagueHref } from '@/lib/routes';
import { useTheme } from '@/lib/styling';
import type { Theme } from '@/theme';
import type {
  CreateLeagueChatMessageRequest,
  LeagueChatMessage,
  LeagueChatMessageListResponse,
  LeagueChatUnreadSummaryEntry,
  LeagueChatUnreadSummaryResponse,
} from '@shared/chat';

const COMPOSER_INPUT_MIN_HEIGHT = 48;
const COMPOSER_INPUT_MAX_HEIGHT = 116;
const COMPOSER_INPUT_LINE_HEIGHT = 20;
const COMPOSER_INPUT_VERTICAL_CHROME = 22;
const IOS_HOME_INDICATOR_MAX = 40;
const IOS_FLOATING_TAB_BAR_HEIGHT = 72;

/**
 * iOS native tabs paint the floating bar over the screen. The first ScrollView
 * is inset automatically; the composer is a sibling, so it needs the same
 * clearance or it sits under the bar. A bottom inset larger than the home
 * indicator already includes an opaque bar.
 */
export function chatComposerBottomClearance(platform: string, safeAreaBottom: number): number {
  if (platform !== 'ios') {
    return 0;
  }
  if (safeAreaBottom > IOS_HOME_INDICATOR_MAX) {
    return safeAreaBottom;
  }
  return Math.max(safeAreaBottom, 12) + IOS_FLOATING_TAB_BAR_HEIGHT;
}

function buildObjectUrl(objectPath: string | null): string | null {
  if (!objectPath) {
    return null;
  }
  return objectPath.startsWith('http') ? objectPath : `${config.apiUrl}${objectPath}`;
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function getMessagePlaceholder(message: LeagueChatMessage): string {
  if (message.deletedAt) {
    return 'Message deleted';
  }
  return message.text ?? '';
}

async function uploadChatImage(leagueId: string, image: PickedImage): Promise<string> {
  const uploadTarget = await apiRequestJson<{ uploadURL: string; objectPath: string }>(
    `/api/leagues/${leagueId}/chat/upload`,
    'POST',
  );

  const fileResponse = await fetch(image.uri);
  const fileBlob = await fileResponse.blob();
  const headers: HeadersInit = image.mimeType ? { 'Content-Type': image.mimeType } : {};
  const uploadResponse = await fetch(uploadTarget.uploadURL, {
    method: 'PUT',
    headers,
    body: fileBlob,
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload image');
  }

  return uploadTarget.objectPath;
}

export default function LeagueChatRoute() {
  const theme = useTheme<Theme>();
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const queryClient = useQueryClient();
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const context = useLeagueContext();
  const { user } = useAuth();
  const { toast } = useToast();
  const { pickImage, takePhoto } = useImagePicker();
  const [draft, setDraft] = useState('');
  const [pendingImage, setPendingImage] = useState<PickedImage | null>(null);
  const isFocused = useIsFocused();
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [keyboardBottomInset, setKeyboardBottomInset] = useState(0);
  const [composerInputHeight, setComposerInputHeight] = useState(COMPOSER_INPUT_MIN_HEIGHT);
  const [composerInputWidth, setComposerInputWidth] = useState(0);
  const lastReadMessageIdRef = useRef<string | null>(null);

  const summaryQueryKey = useMemo(
    () => trpc.chat.summary.queryKey({ leagueId: context.id }),
    [context.id, trpc.chat.summary],
  );
  const messagesQueryKey = useMemo(
    () => trpc.chat.messages.queryKey({ leagueId: context.id, limit: 30 }),
    [context.id, trpc.chat.messages],
  );
  const unreadSummaryFilter = trpc.chat.unreadSummary.queryFilter();
  const bottomTabBarClearance = chatComposerBottomClearance(platformOS, insets.bottom);

  useEffect(() => {
    if (!isIOS) {
      return undefined;
    }

    const handleKeyboardFrameChange = (event: KeyboardEvent) => {
      const keyboardHeight = Math.max(0, windowHeight - event.endCoordinates.screenY);
      setKeyboardBottomInset(keyboardHeight);
    };

    const handleKeyboardHide = () => {
      setKeyboardBottomInset(0);
    };

    const changeSubscription = Keyboard.addListener('keyboardWillChangeFrame', handleKeyboardFrameChange);
    const hideSubscription = Keyboard.addListener('keyboardWillHide', handleKeyboardHide);

    return () => {
      changeSubscription.remove();
      hideSubscription.remove();
    };
  }, [windowHeight]);

  const messagesQuery = useQuery(
    trpc.chat.messages.queryOptions(
      { leagueId: context.id, limit: 30 },
      {
        enabled: context.league?.chatEnabled !== false,
        staleTime: 0,
        refetchInterval: isFocused ? 5_000 : false,
      },
    ),
  );

  const markReadMutation = useMutation({
    mutationFn: async (lastReadMessageId?: string | null) =>
      trpcClient.chat.markRead.mutate({
        leagueId: context.id,
        lastReadMessageId: lastReadMessageId ?? undefined,
      }),
    onSuccess: (summary) => {
      queryClient.setQueryData(summaryQueryKey, summary);
      queryClient.setQueriesData<LeagueChatUnreadSummaryResponse>(unreadSummaryFilter, (current) => {
        if (!current) {
          return current;
        }
        const nextEntry: LeagueChatUnreadSummaryEntry = {
          leagueId: context.id,
          roomId: summary.roomId,
          unreadCount: summary.unreadCount,
          hasUnread: summary.unreadCount > 0,
          latestMessageId: summary.latestMessageId,
          latestMessageAt: summary.latestMessageAt,
        };
        const exists = current.entries.some((entry) => entry.leagueId === context.id);
        return {
          entries: exists
            ? current.entries.map((entry) => (entry.leagueId === context.id ? nextEntry : entry))
            : [...current.entries, nextEntry],
        };
      });
      void queryClient.invalidateQueries({ queryKey: summaryQueryKey });
      void queryClient.invalidateQueries(unreadSummaryFilter);
    },
  });

  const refetchMessages = messagesQuery.refetch;
  useEffect(() => {
    if (!isFocused) {
      return;
    }
    void refetchMessages();
  }, [isFocused, refetchMessages]);

  useEffect(() => {
    const latestMessageId = messagesQuery.data?.messages[messagesQuery.data.messages.length - 1]?.id;
    if (
      !isFocused ||
      !latestMessageId ||
      latestMessageId === lastReadMessageIdRef.current ||
      markReadMutation.isPending
    ) {
      return;
    }
    lastReadMessageIdRef.current = latestMessageId;
    markReadMutation.mutate(latestMessageId);
  }, [isFocused, markReadMutation, messagesQuery.data?.messages]);

  const sendMessageMutation = useMutation({
    mutationFn: async () => {
      let imageObjectPath: string | undefined;
      if (pendingImage) {
        imageObjectPath = await uploadChatImage(context.id, pendingImage);
      }

      const payload: CreateLeagueChatMessageRequest = {
        text: draft.trim() || undefined,
        imageObjectPath,
        imageWidth: pendingImage?.width,
        imageHeight: pendingImage?.height,
      };

      return trpcClient.chat.send.mutate({ leagueId: context.id, ...payload });
    },
    onSuccess: async () => {
      setDraft('');
      setPendingImage(null);
      setComposerInputHeight(COMPOSER_INPUT_MIN_HEIGHT);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: messagesQueryKey }),
        queryClient.invalidateQueries({ queryKey: summaryQueryKey }),
        queryClient.invalidateQueries(unreadSummaryFilter),
      ]);
    },
    onError: () => {
      toast.show({
        type: 'error',
        text1: 'Message failed',
        text2: 'Failed to send message. Please try again.',
      });
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: async (messageId: string) =>
      trpcClient.chat.delete.mutate({ leagueId: context.id, messageId }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: messagesQueryKey }),
        queryClient.invalidateQueries({ queryKey: summaryQueryKey }),
        queryClient.invalidateQueries(unreadSummaryFilter),
      ]);
    },
  });

  const handlePickImage = async () => {
    const image = await pickImage({
      allowsEditing: true,
      quality: 0.75,
      maxFileSizeBytes: 5 * 1024 * 1024,
    });
    if (image) {
      setPendingImage(image);
    }
  };

  const handleTakePhoto = async () => {
    const image = await takePhoto({
      allowsEditing: true,
      quality: 0.75,
      maxFileSizeBytes: 5 * 1024 * 1024,
    });
    if (image) {
      setPendingImage(image);
    }
  };

  const handleAttachmentPress = () => {
    Alert.alert('Add photo', undefined, [
      { text: 'Photo Library', onPress: () => void handlePickImage() },
      { text: 'Camera', onPress: () => void handleTakePhoto() },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const handleDeleteMessage = (message: LeagueChatMessage) => {
    Alert.alert('Delete message', 'This message will be removed for everyone in the league chat.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => deleteMessageMutation.mutate(message.id),
      },
    ]);
  };

  const handleSendMessage = () => {
    if (!canSend || sendMessageMutation.isPending) {
      return;
    }
    sendMessageMutation.mutate();
  };

  const getComposerHeightForText = (text: string, contentHeight = 0) => {
    if (!text) {
      return COMPOSER_INPUT_MIN_HEIGHT;
    }

    const usableWidth = Math.max(1, composerInputWidth - theme.spacing.m * 2 - 6);
    const averageCharacterWidth = 8;
    const charactersPerLine = Math.max(1, Math.floor(usableWidth / averageCharacterWidth));
    const estimatedLineCount = text.split('\n').reduce((lineCount, line) => {
      return lineCount + Math.max(1, Math.ceil(line.length / charactersPerLine));
    }, 0);
    const estimatedHeight = estimatedLineCount * COMPOSER_INPUT_LINE_HEIGHT + COMPOSER_INPUT_VERTICAL_CHROME;

    return Math.min(
      COMPOSER_INPUT_MAX_HEIGHT,
      Math.max(COMPOSER_INPUT_MIN_HEIGHT, Math.ceil(contentHeight), estimatedHeight),
    );
  };

  const handleComposerTextChange = (text: string) => {
    setDraft(text);
    setComposerInputHeight(getComposerHeightForText(text));
  };

  const handleComposerLayout = (event: LayoutChangeEvent) => {
    const nextWidth = Math.ceil(event.nativeEvent.layout.width);
    setComposerInputWidth(nextWidth);
    setComposerInputHeight(getComposerHeightForText(draft));
  };

  const handleComposerContentSizeChange = (
    event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>,
  ) => {
    setComposerInputHeight(getComposerHeightForText(draft, event.nativeEvent.contentSize.height));
  };

  const canSend = draft.trim().length > 0 || Boolean(pendingImage);
  const messageImageHeaders = buildAuthHeaders() as Record<string, string>;
  const composerBottomInset = Math.max(keyboardBottomInset, bottomTabBarClearance);

  if (context.league?.chatEnabled === false) {
    return <Redirect href={getLeagueHref(context.id, context.isMarchMadness ? 'standings' : 'picks')} />;
  }

  return (
    <NativeTabFocusGate>
      <Box flex={1} backgroundColor="background">
        <AppHeader title={context.league?.name ?? 'League Chat'} currentLeagueId={context.id} allowBack={false} />
          <Box
            flex={1}
            gap="m"
            padding="m"
            paddingBottom={composerBottomInset > 0 ? composerBottomInset + theme.spacing.s : 'm'}
          >
            <ScrollView
              style={styles.scroll}
              contentContainerStyle={styles.scrollContent}
              keyboardDismissMode="interactive"
              keyboardShouldPersistTaps="handled"
            >
              {(messagesQuery.data?.messages ?? []).map((message) => {
                const isOwnMessage = message.sender.userId === user?.id;
                const imageUrl = buildObjectUrl(message.imageObjectPath);

                return (
                  <Card key={message.id} style={[styles.messageCard, isOwnMessage ? styles.ownMessageCard : null]}>
                    <CardContent style={styles.messageContent}>
                      <Box flexDirection="row" alignItems="center" justifyContent="space-between">
                          <Box flexDirection="row" alignItems="center" gap="s">
                          <Avatar size={32}>
                            {message.sender.profileImageUrl ? (
                              <AvatarImage src={buildObjectUrl(message.sender.profileImageUrl) ?? undefined} />
                            ) : null}
                          </Avatar>
                          <Box>
                            <Text variant="bodySmall" fontWeight="700">{message.sender.displayName}</Text>
                            <Text variant="caption" color="textSecondary">
                              {formatTimestamp(message.createdAt)}
                              {message.editedAt ? ' · edited' : ''}
                            </Text>
                          </Box>
                        </Box>
                        {message.canDelete ? (
                          <PressableBox onPress={() => handleDeleteMessage(message)} hitSlop={8}>
                            <Trash2 size={16} color={theme.colors.destructive} />
                          </PressableBox>
                        ) : null}
                      </Box>

                      <Text
                        variant="body"
                        color={message.deletedAt ? 'pressMuted' : 'text'}
                        style={message.deletedAt ? styles.deletedText : null}
                      >
                        {getMessagePlaceholder(message)}
                      </Text>

                      {imageUrl ? (
                        <Image
                          source={{ uri: imageUrl, headers: messageImageHeaders }}
                          resizeMode="cover"
                          style={styles.messageImage}
                        />
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })}
              {messagesQuery.data && messagesQuery.data.messages.length === 0 ? (
                <Box flex={1} alignItems="center" justifyContent="center" padding="xl" gap="s">
                  <Text variant="title" fontWeight="700" color="text">No messages yet</Text>
                  <Text variant="bodySmall" color="mutedForeground" textAlign="center">
                    Start the conversation with your league.
                  </Text>
                </Box>
              ) : null}
            </ScrollView>

            {messagesQuery.data?.nextCursor ? (
              <Button
                variant="outline"
                onPress={() => {
                  void queryClient.fetchQuery({
                    queryKey: [...messagesQueryKey, 'older', messagesQuery.data?.nextCursor],
                    queryFn: async () =>
                      trpcClient.chat.messages.query({
                        leagueId: context.id,
                        limit: 30,
                        cursor: messagesQuery.data?.nextCursor ?? undefined,
                      }),
                  }).then((older) => {
                    queryClient.setQueryData<LeagueChatMessageListResponse | undefined>(messagesQueryKey, (current) => {
                      if (!current) {
                        return older;
                      }
                      return {
                        ...older,
                        messages: [...older.messages, ...current.messages],
                      };
                    });
                  });
                }}
              >
                <Text variant="button">Load older messages</Text>
              </Button>
            ) : null}

            <Box style={styles.composer}>
              {pendingImage ? (
                <Box gap="s">
                  <Image source={{ uri: pendingImage.uri }} resizeMode="cover" style={styles.pendingImage} />
                  <Button variant="ghost" onPress={() => setPendingImage(null)}>
                    <Text variant="button">Remove image</Text>
                  </Button>
                </Box>
              ) : null}

              <Box style={styles.composerRow}>
                <Button
                  variant="outline"
                  size="icon"
                  onPress={handleAttachmentPress}
                  accessibilityLabel="Add photo"
                  style={styles.composerIconButton}
                >
                  <ImagePlus size={19} color={theme.colors.text} />
                </Button>
                <TextInput
                  value={draft}
                  onChangeText={handleComposerTextChange}
                  onContentSizeChange={handleComposerContentSizeChange}
                  onLayout={handleComposerLayout}
                  onFocus={() => setIsComposerFocused(true)}
                  onBlur={() => setIsComposerFocused(false)}
                  placeholder="Message the league…"
                  maxLength={2000}
                  multiline
                  scrollEnabled={composerInputHeight >= COMPOSER_INPUT_MAX_HEIGHT}
                  style={[
                    styles.composerInput,
                    { height: composerInputHeight },
                    isComposerFocused ? styles.composerInputFocused : null,
                  ]}
                  placeholderTextColor={theme.colors.mutedForeground}
                />
                <Button
                  onPress={handleSendMessage}
                  disabled={!canSend || sendMessageMutation.isPending}
                  size="icon"
                  accessibilityLabel="Send message"
                  style={styles.composerIconButton}
                >
                  <Send size={16} color={theme.colors.background} />
                </Button>
              </Box>
            </Box>
          </Box>
      </Box>
    </NativeTabFocusGate>
  );
}

const styles = StyleSheet.create((theme: Theme) => ({
  composer: {
    gap: theme.spacing.s,
    justifyContent: 'center',
  },
  composerIconButton: {
    height: 48,
    width: 48,
  },
  composerInput: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadii.m,
    borderWidth: 1,
    color: theme.colors.text,
    flex: 1,
    fontFamily: theme.typography.fontFamily.body,
    fontSize: 15,
    lineHeight: 20,
    maxHeight: COMPOSER_INPUT_MAX_HEIGHT,
    minHeight: COMPOSER_INPUT_MIN_HEIGHT,
    paddingBottom: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
    textAlignVertical: 'top',
  },
  composerInputFocused: {
    borderColor: theme.colors.primary,
  },
  composerRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.s,
    minHeight: 48,
  },
  deletedText: {
    fontStyle: 'italic',
  },
  messageCard: {
    backgroundColor: theme.colors.surface,
  },
  messageContent: {
    gap: theme.spacing.s,
  },
  messageImage: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadii.s,
    height: 220,
    width: '100%',
  },
  ownMessageCard: {
    borderColor: theme.colors.primary,
  },
  pendingImage: {
    borderRadius: theme.borderRadii.s,
    height: 180,
    width: '100%',
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
}));
